import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Avatar } from 'antd';
import { useDispatch } from 'react-redux';
import { randomColor } from '../../utilities/utility';
import { bizGetAccountsMedia } from '../../actions/api-actions';
import logger from '../../services/logger';

/**
 * The AvatarImage function takes in an image, classValue, and type as props and returns either an
 * Avatar component or an img element based on the type prop.
 * @returns The component is returning the result of the `handleShowAvatarImage` function, which is
 * determined based on the `type` prop. If the `type` is 'AVATAR', it returns an `Avatar` component
 * with a background color and the `image` prop as its content. If the `type` is 'IMAGE', it returns an
 * `img` element with the `image` prop
 */
function AvatarImage({ image, classValue, type, sizeVal, shape }) {
  const [profileImage, setProfileImage] = useState('');
  const dispatch = useDispatch();

  /**
   * The function `handleShowAccountImage` is an asynchronous function that retrieves an account's
   * media icon URL and sets it as the profile image.
   * @param iconUrl - The `iconUrl` parameter is the URL of the account's icon image.
   */
  const handleShowAccountImage = async (iconUrl) => {
    try {
      const response = await dispatch(bizGetAccountsMedia(iconUrl));
      if (response?.data?.url) {
        setProfileImage(response?.data?.url);
      } else {
        setProfileImage(require('../../static/img/settings/hoo_services.png'));
      }
    } catch (error) {
      logger.error(error);
    }
  };

  /**  The `useEffect` hook is used to perform side effects in a functional component. In this case, the
    `useEffect` hook is used to call the `handleShowAccountImage` function when the `image` or `type`
    prop changes. */
  useEffect(() => {
    if (image && image !== '' && image !== undefined && image !== null && type === 'IMAGE' && image !== 'www.hooperlabs.com') {
      handleShowAccountImage(image);
    }
  }, [image, type]);

  /** The `handleShowAvatarImage` constant is using the `useMemo` hook to memoize the result of a
      function. This means that the function will only be re-executed if any of the dependencies
      (`image`, `classValue`, `type`) change. */
  const handleShowAvatarImage = useMemo(() => {
    switch (type) {
      /* The `case 'AVATAR':` block is a switch case statement that is executed when the `type` prop is
      equal to `'AVATAR'`. */
      case 'AVATAR': {
        return (
          <span className={classValue}>
            <Avatar size={sizeVal} shape={shape} style={{ backgroundColor: randomColor(image) }}>
              {image}
            </Avatar>
          </span>
        );
      }
      /* The `case 'IMAGE':` block is a switch case statement that is executed when the `type` prop is
      equal to `'IMAGE'`. */
      case 'IMAGE': {
        return (
          <span className={classValue}>
            <img src={profileImage} className={classValue} alt="" />
          </span>
        );
      }
      default: {
        break;
      }
    }
  }, [image, classValue, type, profileImage]);

  return <>{handleShowAvatarImage}</>;
}

AvatarImage.propTypes = {
  image: PropTypes.string,
  classValue: PropTypes.string,
  type: PropTypes.string,
  sizeVal: PropTypes.number,
  shape: PropTypes.string,
};

export default AvatarImage;
